import { useState } from "react";
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useData } from "@/context/DataContext";
import { FinTable } from "@/components/FinTable";
import { KpiCard, Panel, PageHeader, ChartTip, StatusBadge } from "@/components/Widgets";
import { DebtCharts } from "@/pages/ChartPages";
import { cut, fmt, fmtCr, sum, last, PALETTE } from "@/lib/api";
import { loanRows } from "@/pages/entityRows";

export default function Loans() {
  const { data, horizon, filters } = useData();
  const fe = data.model.entities.find((x) => x.code === filters.entity);
  const scope = data.model.entities.filter((e) => !fe || e.code === fe.code);
  const [sel, setSel] = useState(null);
  const years = cut(data.model.years, horizon);
  const loans = scope.flatMap((e) => e.loans.map((l, i) => ({ ...l, entity: e.short, id: `${e.code}-${i}` })));
  const existing = loans.filter((l) => !l.prospective);
  const prospective = loans.filter((l) => l.prospective);
  const intr = sum(loans.flatMap((l) => cut(l.interest, horizon)));
  const bal = sum(loans.flatMap((l) => cut(l.avg_balance, horizon)));
  const ents = scope.filter((e) => e.series.loan_closing.some((v) => v));
  const debt = cut(fe ? fe.series.loan_closing : data.group.debt_total, horizon);
  const chart = years.map((y, i) => {
    const row = { y };
    let t = 0;
    ents.forEach((e) => { row[e.short] = cut(e.series.loan_closing, horizon)[i] * (fe ? 1 : e.switch || 0); t += row[e.short]; });
    if (!fe) row.Other = Math.max(0, debt[i] - t);
    return row;
  });
  const selected = loans.find((l) => l.id === sel);
  const totalOf = (arr, k) => sum(arr.flatMap((l) => cut(l[k], horizon)));
  return (
    <div data-testid="loans-page">
      <PageHeader kicker="Loans sheet & unit loan schedules" title={fe ? `Debt & Interest - ${fe.short}` : "Debt & Interest"} />
      <div className="mb-6"><DebtCharts /></div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testid="loans-kpi-debt" label={`${fe ? fe.short : "Group"} debt FY ${last(years)}`} value={fmtCr(last(debt))} tone="neg" sub={`Peak ${fmtCr(Math.max(...debt))}`} />
        <KpiCard testid="loans-kpi-existing" label="Existing facilities" value={existing.length} sub={`Repayments ${fmtCr(totalOf(existing, "repayment"))}`} tone="blue" />
        <KpiCard testid="loans-kpi-prospective" label="Prospective facilities" value={prospective.length} sub={`Drawdowns ${fmtCr(totalOf(prospective, "drawdown"))}`} />
        <KpiCard testid="loans-kpi-rate" label="Blended interest rate" value={bal ? `${((Math.abs(intr) / bal) * 100).toFixed(2)}%` : "–"} sub={`Interest ${fmtCr(intr)} over horizon`} tone="amber" />
      </div>
      <Panel className="mt-6" title="Closing debt by entity" subtitle="'Other' = corporate facilities in the Loans sheet without a unit sheet" testid="loans-chart">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chart}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="y" tickLine={false} axisLine={false} />
            <YAxis tickLine={false} axisLine={false} tickFormatter={fmt} width={60} />
            <Tooltip content={<ChartTip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {ents.map((e, i) => <Area key={e.code} dataKey={e.short} stackId="d" stroke={PALETTE[i]} fill={PALETTE[i]} fillOpacity={0.35} />)}
            <Area dataKey="Other" stackId="d" stroke="#64748B" fill="#64748B" fillOpacity={0.35} />
          </AreaChart>
        </ResponsiveContainer>
      </Panel>
      <Panel className="mt-6" title="Facility register" subtitle="Click a facility to view its schedule" testid="loans-register">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                {["Entity", "Facility", "Type", "Lender", "Status", "Rate", `Opening FY ${years[0]}`, "Drawdown", "Repayment", `Closing FY ${last(years)}`].map((h) => (
                  <th key={h} className="py-2 pr-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loans.map((l) => (
                <tr key={l.id} onClick={() => setSel(l.id === sel ? null : l.id)} data-testid={`loan-row-${l.id}`}
                  className={`cursor-pointer border-t border-slate-800 transition-colors hover:bg-slate-800/40 ${sel === l.id ? "bg-amber-500/5" : ""}`}>
                  <td className="py-2 pr-3 text-slate-300">{l.entity}</td>
                  <td className="py-2 pr-3 text-slate-100">{l.name}</td>
                  <td className="py-2 pr-3 text-slate-400">{l.type || "—"}</td>
                  <td className="py-2 pr-3 text-slate-400">{l.lender || "—"}</td>
                  <td className="py-2 pr-3"><StatusBadge ok={!l.prospective}>{l.prospective ? "Prospective" : "Existing"}</StatusBadge></td>
                  <td className="num py-2 pr-3 text-amber-400">{l.rate != null ? `${(l.rate * 100).toFixed(2)}%` : "–"}</td>
                  <td className="num py-2 pr-3">{fmt(cut(l.opening, horizon)[0])}</td>
                  <td className="num py-2 pr-3">{fmt(sum(cut(l.drawdown, horizon)))}</td>
                  <td className="num py-2 pr-3">{fmt(sum(cut(l.repayment, horizon)))}</td>
                  <td className="num py-2">{fmt(last(cut(l.closing, horizon)))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {selected && (
          <div className="mt-5" data-testid="loan-schedule">
            <div className="mb-2 text-sm font-semibold text-slate-100">{selected.entity} · {selected.name} schedule</div>
            <FinTable years={years} rows={loanRows(selected, horizon)} testid="loan-schedule-table" maxH="none" />
          </div>
        )}
      </Panel>
    </div>
  );
}
